import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import de from './locales/de.json';
import en from './locales/en.json';
import fr from './locales/fr.json';

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight, dependency-free i18n. Translations live in per-language JSON
// dictionaries (curated by hand, not machine-translated) keyed by flat dotted
// keys. The selected locale is persisted in localStorage and mirrored onto
// <html lang>. Components read strings via useT().t('some.key').
//
// German is the source/fallback language: any key missing from en/fr falls back
// to the German value (and finally to the key itself), so a half-finished
// translation never renders a blank.
// ─────────────────────────────────────────────────────────────────────────────

export type LangCode = 'de' | 'en' | 'fr';

export const LOCALES: {
    code: string; // full BCP-47 tag, persisted (e.g. "de-DE")
    lang: LangCode; // dictionary key
    label: string; // short code shown in the menu
    country: string; // shown next to the flag (native name)
    flag: string;
}[] = [
    { code: 'de-DE', lang: 'de', label: 'DE', country: 'Deutschland', flag: '🇩🇪' },
    { code: 'en-GB', lang: 'en', label: 'EN', country: 'United Kingdom', flag: '🇬🇧' },
    { code: 'fr-FR', lang: 'fr', label: 'FR', country: 'France', flag: '🇫🇷' },
];

const DICTS: Record<LangCode, Record<string, string>> = {
    de: de as Record<string, string>,
    en: en as Record<string, string>,
    fr: fr as Record<string, string>,
};

const LOCALE_KEY = 'me.locale';

type Vars = Record<string, string | number>;

type I18nValue = {
    /** Full BCP-47 tag, e.g. "de-DE". */
    code: string;
    /** Dictionary language, e.g. "de". */
    lang: LangCode;
    /** Switch language by full code (persists + updates <html lang>). */
    setCode: (code: string) => void;
    /** Translate a key with optional {placeholder} interpolation. */
    t: (key: string, vars?: Vars) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

function langFor(code: string): LangCode {
    return LOCALES.find((l) => l.code === code)?.lang ?? 'de';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
    const [code, setCode] = useState<string>(() => {
        try {
            return localStorage.getItem(LOCALE_KEY) || 'de-DE';
        } catch {
            return 'de-DE';
        }
    });

    const lang = langFor(code);

    useEffect(() => {
        try {
            localStorage.setItem(LOCALE_KEY, code);
        } catch {
            /* storage unavailable */
        }
        document.documentElement.lang = lang;
    }, [code, lang]);

    const t = useMemo(() => {
        const dict = DICTS[lang] || DICTS.de;
        return (key: string, vars?: Vars) => {
            let s = dict[key] ?? DICTS.de[key] ?? key;
            if (vars) {
                for (const k of Object.keys(vars)) {
                    s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
                }
            }
            return s;
        };
    }, [lang]);

    const value = useMemo<I18nValue>(() => ({ code, lang, setCode, t }), [code, lang, t]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nValue {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error('useT must be used within <I18nProvider>');
    return ctx;
}
